// src/pages/SectorOverview.jsx
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Button,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";

import { supabase } from "../utils/supabaseClient";
import PublicLayout from "../components/PublicLayout";
import SectorMapView from "../components/SectorMapView";

export default function SectorOverview() {
  const { sector } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [geoData, setGeoData] = useState(null);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from("sector_intelligence")
        .select("*")
        .eq("sector", sector.toUpperCase())
        .single();

      setInfo(data);

      const geo = await supabase.from("uk_geojson").select("*").single();
      if (geo?.data?.geojson) {
        setGeoData(geo.data.geojson);
      }
    }

    load();
  }, [sector]);

  if (!info) {
    return (
      <PublicLayout>
        <Box p={6}>
          <Text fontSize="lg" color="gray.600">
            Loading sector insights…
          </Text>
        </Box>
      </PublicLayout>
    );
  }

  const sectorLabel = sector.toUpperCase();
  const canonicalUrl = `https://postcode.coupons/sector/${sector}`;

  return (
    <PublicLayout>
      <Helmet>
        <title>{sectorLabel} Sector Overview | Postcode Coupons & Local Insights</title>
        <meta
          name="description"
          content={`Households, postcode coupons, Coupon Flyer availability, and print schedule for postcode sector ${sectorLabel}.`}
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <Box p={6} maxW="900px" mx="auto">
        <Text fontSize="4xl" fontWeight="bold" mb={2}>
          Sector {sectorLabel}
        </Text>

        <Text fontSize="md" color="gray.600" mb={6}>
          Local insights, postcode coupons coverage, Coupon Flyer schedule, and booking availability.
        </Text>

        {geoData && (
          <Box
            height="260px"
            borderRadius="md"
            overflow="hidden"
            boxShadow="sm"
            mb={8}
          >
            <SectorMapView
              geoData={geoData}
              mapCenter={[54.5, -3]}
              usingUkFallback={false}
              highlightSector={sector}
              onSectorClick={() => {}}
            />
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
          <Stat p={5} borderRadius="lg" boxShadow="sm" bg="white" border="1px solid #eee">
            <StatLabel>Households reached</StatLabel>
            <StatNumber>{info.households}</StatNumber>
            <StatHelpText>Delivery coverage</StatHelpText>
          </Stat>

          <Stat p={5} borderRadius="lg" boxShadow="sm" bg="white" border="1px solid #eee">
            <StatLabel>Slots available</StatLabel>
            <StatNumber>{info.slotsAvailable}</StatNumber>
            <StatHelpText>Next Coupon Flyer</StatHelpText>
          </Stat>

          <Stat p={5} borderRadius="lg" boxShadow="sm" bg="white" border="1px solid #eee">
            <StatLabel>Next print</StatLabel>
            <StatNumber>{info.daysUntilPrint} days</StatNumber>
            <StatHelpText>Until dispatch</StatHelpText>
          </Stat>
        </SimpleGrid>

        <Divider my={8} />

        <Flex gap={4} flexWrap="wrap">
          <Button colorScheme="red" size="lg" onClick={() => navigate(`/flyer/${sector}`)}>
            View postcode coupons
          </Button>

          <Button size="lg" variant="outline" onClick={() => navigate("/signup")}>
            Book a slot
          </Button>

          <Button size="lg" variant="ghost" onClick={() => navigate("/map")}>
            View on map
          </Button>
        </Flex>
      </Box>
    </PublicLayout>
  );
}
